import { AppBar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { formatNumber } from '../utils/numbersFormatter'
import { useTranslation } from 'react-i18next'
import { ClientTaxSummary, CurrencyAmount, ProfitLossDeal } from '../redux/reducers/clients'
import { NewLayout } from '../components/newLayout/NewLayout'
import { newThemeObject } from '../newTheme'
import { OrderDetailDialog } from '../components/dialogs/OrderDetailDialog'
import { FileImportDialog } from '../components/dialogs/FileImportDialog'
import CardWrapper from '../components/common/CardWrapper'
import { YearQuickFilter } from '../components/common/YearQuickFilter'
import classNames from 'classnames'
import { useSelector } from '../redux/hooks/useSelector'
import { LoadingIndicator } from '../components/common/LoadingIndicator'
import { useClientApi } from '../hooks/useClientApi'
import { getClientIdFromUser, isClientUser } from '../services/keycloak.service'
import { DatePicker, LocalizationProvider } from '@mui/lab'
import AdapterDateFns from '@date-io/date-fns'
import { ColoredIconButton } from '../components/common/ColoredIconButton'
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import { AlertMessageConfirmation } from '../components/dialogs/AlertMessageConfirmation'
import { tokens } from '../styles/tokens'
import { toApiDateTime } from '../utils/apiDateTime'

const CURRENT_YEAR = new Date().getFullYear()
const QUICK_FILTER_YEARS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR]

const useStyles = makeStyles(theme => ({
  title: {
    color: tokens.color.white,
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  paper: {
    overflowY: 'auto',
    overflowX: 'auto',
    gap: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '20px',
  },
  topBar: {
    background: `linear-gradient(135deg, ${tokens.color.primary[800]}, ${tokens.color.primary[600]})`,
    margin: '-16px -16px 20px',
    padding: '12px 16px',
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} 0 0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minHeight: '48px',
  },
  note1: {
    marginLeft: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    color: tokens.color.neutral[600],
  },
  table: {
    minWidth: 1000,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  tableHead: {
    ...newThemeObject.table.tableHead,
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  tableContent: {
    ...newThemeObject.table.tableContent,
  },
  tableContentFirst: {
    ...newThemeObject.table.tableContentFirst,
  },
  firstClickableRow: {
    cursor: 'pointer',
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  lastRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
  },
  tableRow: {
    '& .MuiTableCell-root': {
      borderBottom: 'none',
      borderTop: 'thin solid',
      borderColor: tokens.color.neutral[200],
    },
  },
  text: {
    fontWeight: '400',
    margin: '0',
    fontSize: '0.7rem',
    textWrap: 'wrap',
  },

  tableTopBar: {
    backgroundColor: tokens.color.white,
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
    borderRadius: tokens.radius.lg,
    marginBottom: theme.spacing(2),
  },
  tableToolBar: {
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    alignContent: 'space-around',
    width: '100%',
    padding: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    margin: 0,
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  textField: {
    width: '100%',
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginRight: theme.spacing(1.5),
      maxWidth: '180px',
    },
  },
  yearButtons: {
    marginTop: theme.spacing(1.5),
    [theme.breakpoints.up('md')]: {
      marginTop: 0,
      marginRight: theme.spacing(1.5),
    },
  },
  filler: {
    flexGrow: 1,
  },
  icon: {
    marginLeft: theme.spacing(0.5),
  },
}))

export const ClientDetailTaxSummary = () => {
  const classes = useStyles()

  const loading = useSelector(state => state.clients.loading)
  const client = useSelector(state => state.clients.client)
  const clientTaxSummaryTmp = useSelector(state => state.clients.clientTaxSummary)

  const { getClientTaxSummary, exportSummary } = useClientApi()
  const { getClient } = useClientApi()
  const { clientId } = useParams<{ clientId: string }>()
  const { t } = useTranslation()

  const getClientId = () => {
    if (isClientUser()) {
      return getClientIdFromUser()
    } else {
      return clientId
    }
  }
  
  const clientId2 = getClientId()
  useEffect(() => {
    if ( !client )
      getClient(Number(clientId2))
  }, [client])


  // Vychozi obdobi je aktualni rok - souhrn bez obdobi by scital celou historii,
  // coz pro danove priznani nedava smysl (viz CH16 v docs/07-chyby-a-reseni).
  const [dateFrom, setDateFrom] = useState<Date | undefined>(new Date(CURRENT_YEAR, 0, 1));
  const [dateTo, setDateTo] = useState<Date | undefined>(new Date());
  const [selectedYear, setSelectedYear] = useState<number | null>(CURRENT_YEAR);

  useEffect(()=>{
    if ( client && client.clientId!==0 ) {
      getClientTaxSummary(client.clientId, dateFrom ? toApiDateTime(dateFrom) : '', dateTo ? toApiDateTime(dateTo):'');
    }
  }, [client, dateFrom, dateTo])

  const [clientTaxSummary, setClientTaxSummary] = useState<ClientTaxSummary>();

  const onDateFromChange = (name: string) => (date: Date | null) => {
    setDateFrom(date || undefined)
    setSelectedYear(null)
  }

  const onDateToChange = (name: string) => (date: Date | null) => {
    setDateTo(date || new Date())
    setSelectedYear(null)
  }

  const onYearSelect = (year: number) => {
    const now = new Date()
    const isCurrentYear = year === now.getFullYear()
    setDateFrom(new Date(year, 0, 1))
    setDateTo(isCurrentYear ? now : new Date(year, 11, 31, 23, 59, 59, 999))
    setSelectedYear(year)
  }

  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  
  const handleConfirmation = () => {
    if ( clientTaxSummary ) {
      exportSummary(clientTaxSummary, clientId, dateFrom?toApiDateTime(dateFrom):'', dateTo?toApiDateTime(dateTo):'');
    }

    setShowConfirmation(false);
  }

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  }

  useEffect(()=>{
    if ( clientTaxSummaryTmp ) {     
      setClientTaxSummary({...clientTaxSummaryTmp});
    }
  }, [clientTaxSummaryTmp, dateFrom, dateTo])


  return (
    <NewLayout pageName="TAX_REPORT_SUMMARY">

      <AppBar position="static" className={classes.tableTopBar} elevation={0}>
      <Toolbar variant="dense" className={classes.tableToolBar}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            renderInput={params => <TextField {...params} variant="standard" className={classes.textField} label={t('DATE_FROM2')}/>}
            inputFormat="dd/MM/yyyy"
            value={dateFrom || null}
            onChange={onDateFromChange('dateFromId')}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            renderInput={params => (
              <TextField {...params} variant="standard" className={classes.textField} label={t('DATE_TO')} />
            )}
            inputFormat="dd/MM/yyyy"
            value={dateTo || null}
            onChange={onDateToChange('dateToId')}
          />
        </LocalizationProvider>

        <YearQuickFilter
          className={classes.yearButtons}
          years={QUICK_FILTER_YEARS}
          selectedYear={selectedYear}
          onSelectYear={onYearSelect}
        />

        <span className={classes.filler} />

        <ColoredIconButton
          className={classes.icon}
          onClick={() => {
            setShowConfirmation(true);
          }}
          size="small"
          icon={<DownloadIcon />}
        />
      </Toolbar>
      </AppBar>

      {loading && <LoadingIndicator />}
      <React.Fragment key={'summary_01'}>
        <TableContainer component={CardWrapper} className={classes.paper} key={66}>
          <span className={classes.topBar}>
            <p className={classes.title}>{t("TAX_SUMMARY_DEALS")}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_DEALS_NOTE1')}</p>
          </span>
          {!loading && clientTaxSummary && clientTaxSummary.deals && clientTaxSummary.deals.length>0 &&
            <Table className={classes.table} size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                    <p className={classes.tableHeadChildrenText}>{t('DEAL_DESCRIPTION')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'center'} }>
                    <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('REVENUE')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('COST')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('PROFIT_LOSS')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} />
                </TableRow>
              </TableHead>
              <TableBody>
                {clientTaxSummary.deals.map((clientDeal: ProfitLossDeal, index: number) => (
                  <React.Fragment key={index}>
                    <TableRow
                      className={classNames(index === 0 ? classes.firstClickableRow : classes.tableRow)}
                    >
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{t('TAX_SUMMARY_DEALS_TEXT')}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent} style={{textAlign: 'center'}}>
                        <p className={classes.text}>{clientDeal.currency}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.revenue)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.cost)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.profitLoss)}</p>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          }
          {!loading && clientTaxSummary && clientTaxSummary.deals && clientTaxSummary.deals.length===0 &&
            <span className={classes.note1}>
              <p className={classes.text}>{t('TAX_SUMMARY_NO_DATA')}</p>
            </span>
          }
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_DEALS_NOTE2')}</p>
          </span>
        </TableContainer>
      </React.Fragment>

      <React.Fragment key={'summary_012'}>
        <TableContainer component={CardWrapper} className={classes.paper} key={66}>
          <span className={classes.topBar}>
            <p className={classes.title}>{t("TAX_SUMMARY_DEALS2")}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_DEALS_NOTE1')}</p>
          </span>
          {!loading && clientTaxSummary && clientTaxSummary.dealsNoTax && clientTaxSummary.dealsNoTax.length>0 &&

            <Table className={classes.table} size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                    <p className={classes.tableHeadChildrenText}>{t('DEAL_DESCRIPTION')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'center'} }>
                    <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('REVENUE')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('COST')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('PROFIT_LOSS')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} />
                </TableRow>
              </TableHead>
              <TableBody>
                {clientTaxSummary.dealsNoTax.map((clientDeal: ProfitLossDeal, index: number) => (
                  <React.Fragment key={index}>
                    <TableRow
                      className={classNames(index === 0 ? classes.firstClickableRow : classes.tableRow)}
                    >
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{t('TAX_SUMMARY_DEALS_TEXT')}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent} style={{textAlign: 'center'}}>
                        <p className={classes.text}>{clientDeal.currency}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.revenue)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.cost)}</p>
                      </TableCell>
                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.profitLoss)}</p>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          }
          {!loading && clientTaxSummary && clientTaxSummary.dealsNoTax && clientTaxSummary.dealsNoTax.length===0 &&
            <span className={classes.note1}>
              <p className={classes.text}>{t('TAX_SUMMARY_NO_DATA')}</p>
            </span>
          }
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_DEALS_NOTE3')}</p>
          </span>
        </TableContainer>
      </React.Fragment>
      

      <React.Fragment key={'summary_02'}>
        <TableContainer component={CardWrapper} className={classes.paper} key={66}>
          <span className={classes.topBar}>
            <p className={classes.title}>{t("TAX_SUMMARY_YIELDS")}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_YIELDS_NOTE1')}</p>
          </span>
          {!loading && clientTaxSummary && clientTaxSummary.coupons && clientTaxSummary.coupons.length>0 &&
            <Table className={classes.table} size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                    <p className={classes.tableHeadChildrenText}>{t('YIELD_DESCRIPTION')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'center'} }>
                    <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('AMOUNT')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('TAX')}</p>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>

                {clientTaxSummary.dividends.map((clientDeal: CurrencyAmount, index: number) => (
                  <React.Fragment key={index}>
                    <TableRow
                      className={classNames(index === 0 ? classes.firstClickableRow : classes.tableRow)}
                    >
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{t('DIVIDENDS')}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'center'}}>
                        <p className={classes.text}>{clientDeal.currency}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.amount)}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(0)}</p>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}

                {clientTaxSummary.coupons.map((clientDeal: CurrencyAmount, index: number) => (
                  <React.Fragment key={index}>
                    <TableRow
                      className={classNames(index === 0 && clientTaxSummary.dividends.length === 0 ? classes.firstClickableRow : classes.tableRow)}
                    >
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{t('COUPONS')}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'center'}}>
                        <p className={classes.text}>{clientDeal.currency}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.amount)}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(0)}</p>
                      </TableCell>

                    </TableRow>
                  </React.Fragment>
                ))}

              </TableBody>
            </Table>
          }
          {!loading && clientTaxSummary && clientTaxSummary.coupons && clientTaxSummary.coupons.length===0 &&
            <span className={classes.note1}>
              <p className={classes.text}>{t('TAX_SUMMARY_NO_DATA')}</p>
            </span>
          }
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_YIELDS_NOTE2')}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_YIELDS_NOTE3')}</p>
          </span>
        </TableContainer>
      </React.Fragment>

      <React.Fragment key={'summary_022'}>
        <TableContainer component={CardWrapper} className={classes.paper} key={66}>
          <span className={classes.topBar}>
            <p className={classes.title}>{t("TAX_SUMMARY_YIELDS2")}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_YIELDS_NOTE1')}</p>
          </span>
          {!loading && clientTaxSummary && clientTaxSummary.couponsNoTax && clientTaxSummary.couponsNoTax.length>0 &&
            <Table className={classes.table} size="small">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])}>
                    <p className={classes.tableHeadChildrenText}>{t('YIELD_DESCRIPTION')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'center'} }>
                    <p className={classes.tableHeadChildrenText}>{t('CURRENCY')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('AMOUNT')}</p>
                  </TableCell>
                  <TableCell className={classes.tableHeadChildren} style={{textAlign: 'right'}}>
                    <p className={classes.tableHeadChildrenText}>{t('TAX')}</p>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>

                {clientTaxSummary.dividendsNoTax.map((clientDeal: CurrencyAmount, index: number) => (
                  <React.Fragment key={index}>
                    <TableRow
                      className={classNames(index === 0 ? classes.firstClickableRow : classes.tableRow)}
                    >
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{t('DIVIDENDS')}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'center'}}>
                        <p className={classes.text}>{clientDeal.currency}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.amount)}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{t('NO_TAX')}</p>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}

                {clientTaxSummary.couponsNoTax.map((clientDeal: CurrencyAmount, index: number) => (
                  <React.Fragment key={index}>
                    <TableRow
                      className={classNames(index === 0 && clientTaxSummary.dividendsNoTax.length === 0 ? classes.firstClickableRow : classes.tableRow)}
                    >
                      <TableCell className={classes.tableContentFirst}>
                        <p className={classes.text}>{t('COUPONS')}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'center'}}>
                        <p className={classes.text}>{clientDeal.currency}</p>
                      </TableCell>
                      
                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{formatNumber(clientDeal.amount)}</p>
                      </TableCell>

                      <TableCell className={classes.tableContent} style={{textAlign: 'right'}}>
                        <p className={classes.text}>{t('NO_TAX')}</p>
                      </TableCell>

                    </TableRow>
                  </React.Fragment>
                ))}

              </TableBody>
            </Table>
          }
          {!loading && clientTaxSummary && clientTaxSummary.couponsNoTax && clientTaxSummary.couponsNoTax.length===0 &&
            <span className={classes.note1}>
              <p className={classes.text}>{t('TAX_SUMMARY_NO_DATA')}</p>
            </span>
          }
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_YIELDS_NOTE2')}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t('TAX_SUMMARY_YIELDS_NOTE3')}</p>
          </span>
        </TableContainer>
      </React.Fragment>

      <React.Fragment key={'summary_03'}>
        <TableContainer component={CardWrapper} className={classes.paper} key={66}>
          <span className={classes.note1}>
            <p className={classes.text}>{t("TAX_SUMMARY_NOTE1")}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t("TAX_SUMMARY_NOTE2")}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t("TAX_SUMMARY_NOTE3")}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t("TAX_SUMMARY_NOTE4")}</p>
          </span>
          <span className={classes.note1}>
            <p className={classes.text}>{t("TAX_SUMMARY_NOTE5")}</p>
          </span>
        </TableContainer>
      </React.Fragment>

      <OrderDetailDialog />
      <FileImportDialog />
      <AlertMessageConfirmation
        alertOpen={showConfirmation}
        onOk={handleConfirmation}
        onCancle={handleCloseConfirmation}
        alertText={t('TAX_SUMMARY_EXPORT_MESSAGE')}
      />
    </NewLayout>
  )
}
